import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Switch, Route, withRouter, Redirect } from 'react-router-dom';

import routes from 'bento-ordering/base/routes';
import modalRoutes from 'bento-ordering/base/routes/modal';
import { closeModal } from 'bento-ordering/base/actions/modal';

import { getOfflinePageDestination } from 'bento-ordering/locations/selectors';
import SetupInitialFlow from 'bento-ordering/base/containers/SetupInitialFlow';
import {
  initializeTracking,
  trackPageview,
  setReferralSource,
  setUTMCampaign
} from 'bento-ordering/base/actions/tracking';
import { setGoogleLoaded } from 'bento-ordering/base/actions/libraryLoad';
import initializeGoogle from 'helpers/initializeGoogle';
import isBentoOrderingRoute from 'bento-ordering/base/utils/isBentoOrderingRoute';
import MenuManager from 'bento-ordering/menus/containers/MenuManager';
import GroupOrderManager from 'bento-ordering/group_ordering/containers/GroupOrderManager';
import BentoStreamManager from 'containers/BentoStreamManager';
import { MenuProvider } from 'bento-ordering/context/MenuContext';
import { LayoutProvider } from 'bento-ordering/context/LayoutContext';
import BentoOrderingExternalPromotionToast from 'bento-ordering/promotions/components/BentoOrderingExternalPromotionToast';
import PreviewModeManager from 'components/PreviewModeManager';

class App extends React.PureComponent {
  static propTypes = {
    location: PropTypes.object,
    dispatch: PropTypes.func,
    isModalOpen: PropTypes.bool,
    offlinePageDestination: PropTypes.string,
    account: PropTypes.object,
    menu: PropTypes.shape({ id: PropTypes.number }),
    history: PropTypes.shape({ listen: PropTypes.func }).isRequired
  };

  previousLocation = this.props.location;

  componentDidMount() {
    this.props.dispatch(initializeTracking());
    this.props.dispatch(trackPageview('', false));
    this.props.dispatch(setReferralSource());
    this.props.dispatch(setUTMCampaign());
    initializeGoogle(() => {
      this.props.dispatch(setGoogleLoaded());
    });
  }

  // keep track of the location as the previous if the location
  // is not a modal location
  componentDidUpdate(prevProps) {
    const { dispatch, isModalOpen, offlinePageDestination } = this.props;
    if (!isModalOpen) {
      this.previousLocation = this.props.location;
    }

    this.handleAnalytics(prevProps);

    /**
     * close all modals if user try to go to a modal route and
     * we are supposed to redirect to offline page
     */

    if (offlinePageDestination) {
      dispatch(closeModal());
    }
  }

  handleAnalytics(prevProps) {
    const { location, account, dispatch } = this.props;
    if (
      location.pathname !== prevProps.location.pathname ||
      (prevProps.account.name && prevProps.account.name !== account.name)
    ) {
      dispatch(trackPageview('', true));
    }
  }

  renderRoute = ({
    props,
    Layout,
    Component,
    componentProps,
    redirect,
    previousLocation,
    isModal
  }) => {
    const { offlinePageDestination } = this.props;
    if (offlinePageDestination) {
      return <Redirect to={offlinePageDestination} />;
    }
    if (redirect) {
      return (
        <Redirect exact to={redirect({ slug: props.match.params.slug })} />
      );
    }

    return (
      <React.Fragment>
        <Layout isModal={isModal} {...props} {...componentProps}>
          <BentoOrderingExternalPromotionToast />
          <Component
            {...props}
            {...componentProps}
            previousLocation={previousLocation}
          />
        </Layout>
      </React.Fragment>
    );
  };

  renderRoutes() {
    return routes.map(route => {
      return (
        <Route
          key={route.path}
          render={props =>
            this.renderRoute({
              props,
              Layout: route.Layout,
              Component: route.Component,
              componentProps: route.componentProps,
              redirect: route.redirect,
              previousLocation: this.previousLocation
            })
          }
          path={route.path}
          exact={route.exact}
        />
      );
    });
  }

  /**
   * Renders a set of routes that will render as a modal. Some
   * of these route paths will match normal normal route paths
   * because there are cases where we may have the same route, but
   * on mobile will render it as a normal page route while desktop
   * will render as a modal route.
   */
  renderModalRoutes() {
    return modalRoutes.map(route => {
      return (
        <Route
          key={route.path}
          render={props =>
            this.renderRoute({
              props,
              Layout: route.Layout,
              Component: route.Component,
              componentProps: route.componentProps,
              previousLocation: this.previousLocation,
              isModal: true
            })
          }
          path={route.path}
          exact={route.exact}
        />
      );
    });
  }

  /**
   * renders the App, including:
   * PreviewModeManager: handles disabling navigation outside of the app when isPreview
   * MenuManager: (which controls fetching of
   * menu and other resources necessary for rendering Menu page),
   * SetupInitialFlow, and our routes (including modal routes)
   *
   * We render menu manager at the app level so that on mobile, we
   * always have a rendered menu manager that can observe changes to
   * cart fulfillment options to know when to fetch a new menu
   */
  renderApp = () => {
    const { isModalOpen } = this.props;

    return (
      <>
        <PreviewModeManager />
        <MenuManager />
        <GroupOrderManager />
        <BentoStreamManager />
        <SetupInitialFlow>
          <MenuProvider>
            <LayoutProvider>
              <Switch
                location={
                  isModalOpen ? this.previousLocation : this.props.location
                }
              >
                {this.renderRoutes()}
              </Switch>
              {isModalOpen && <Switch>{this.renderModalRoutes()}</Switch>}
            </LayoutProvider>
          </MenuProvider>
        </SetupInitialFlow>
      </>
    );
  };

  render() {
    // block render if we are not on the a supported route
    if (!isBentoOrderingRoute()) {
      return null;
    }

    return this.renderApp();
  }
}

function mapStateToProps(state) {
  const { modal, account, menu } = state;
  return {
    isModalOpen: modal.isOpen,
    offlinePageDestination: getOfflinePageDestination(state),
    account,
    menu
  };
}

export default withRouter(connect(mapStateToProps)(App));
